import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowRight, Sun, Moon, KeyRound, Mail, CheckCircle,
  MessageCircle, Brain, Target, Shield, Sparkles
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_DARK = 'https://customer-assets.emergentagent.com/job_leadership-hub-59/artifacts/yr2ulz5t_Brainayan-Logo-HighRes.png';
const LOGO_LIGHT = 'https://customer-assets.emergentagent.com/job_leadership-hub-59/artifacts/ugsfbpeg_Brainayan%20Logo%20-Light-%20Transparent_BG.png';
const HERO_BG = 'https://images.unsplash.com/photo-1709626011485-6fe000ea2dbc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMGRhcmslMjB0ZWNoJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3ODQwMTM5OTh8MA&ixlib=rb-4.1.0&q=85';

export default function LoginPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register, user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [resetTokenInput, setResetTokenInput] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  useEffect(() => { if (user) navigate('/'); }, [user, navigate]);
  if (user) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) { await login(email, password); }
      else { await register(name, email, password); }
      toast.success(isLogin ? 'Welcome back!' : 'Account created!');
      navigate('/');
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(e => e.msg || '').join(' ') : 'Something went wrong';
      toast.error(msg);
    } finally { setLoading(false); }
  };

  const handleForgotPassword = async () => {
    if (!forgotEmail.trim()) return toast.error('Enter your email');
    setForgotLoading(true);
    try {
      const res = await axios.post(`${API}/api/auth/forgot-password`, { email: forgotEmail.trim() });
      if (res.data.reset_token) setResetTokenInput(res.data.reset_token);
      toast.success('Reset token generated!');
      setShowForgot(false);
      setShowReset(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to process request');
    } finally { setForgotLoading(false); }
  };

  const handleResetPassword = async () => {
    if (!resetTokenInput.trim()) return toast.error('Enter the reset token');
    if (newPassword.length < 6) return toast.error('Password must be at least 6 characters');
    if (newPassword !== confirmPassword) return toast.error('Passwords do not match');
    setResetLoading(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, { token: resetTokenInput.trim(), new_password: newPassword });
      setResetSuccess(true);
      toast.success('Password reset successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally { setResetLoading(false); }
  };

  const FEATURES = [
    { icon: MessageCircle, label: 'Feedback & Conversations', color: '#2BA5A5' },
    { icon: Brain, label: 'Critical Thinking', color: '#E87722' },
    { icon: Target, label: 'Decision Making', color: '#4A90D9' },
    { icon: Shield, label: 'Situational Leadership', color: '#B0CB1F' },
  ];

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Hero Panel */}
      <div className="hidden lg:flex lg:w-[52%] relative overflow-hidden" style={{ backgroundColor: '#333333' }}>
        {/* Background image overlay */}
        <img src={HERO_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-10 mix-blend-overlay" />
        
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-80 h-80 rounded-full opacity-5" style={{ background: 'radial-gradient(circle, #B0CB1F 0%, transparent 70%)', transform: 'translate(30%, -30%)' }} />
        <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full opacity-5" style={{ background: 'radial-gradient(circle, #2BA5A5 0%, transparent 70%)', transform: 'translate(-30%, 30%)' }} />

        <div className="relative z-10 flex flex-col justify-between p-12 xl:p-16 w-full">
          {/* Logo */}
          <div>
            <img src={LOGO_LIGHT} alt="Brainayan" className="h-9 w-auto" data-testid="login-logo-hero" />
          </div>

          {/* Main content */}
          <div className="space-y-10">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] mb-4" style={{ color: '#B0CB1F' }}>
                AI-Powered Simulation Platform
              </p>
              <h1 className="text-4xl xl:text-5xl font-bold text-white leading-[1.1] tracking-tight" style={{ fontFamily: 'system-ui' }}>
                Master workplace<br />conversations with<br />
                <span style={{ color: '#B0CB1F' }}>confidence</span>
              </h1>
              <p className="text-base text-white/50 mt-5 max-w-md leading-relaxed">
                Practice giving feedback, handling difficult conversations, and developing leadership skills through realistic AI simulations.
              </p>
            </div>

            {/* Feature chips */}
            <div className="flex flex-wrap gap-2.5">
              {FEATURES.map((f, i) => (
                <div key={i} className="flex items-center gap-2 px-3.5 py-2 rounded-lg backdrop-blur-sm"
                  style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <f.icon className="w-4 h-4" style={{ color: f.color }} />
                  <span className="text-xs font-medium text-white/80">{f.label}</span>
                </div>
              ))}
            </div>

            {/* Stats row */}
            <div className="flex items-center gap-10">
              {[
                { num: '5+', label: 'Skill Areas', color: '#B0CB1F' },
                { num: '15+', label: 'Scenarios', color: '#2BA5A5' },
                { num: '10', label: 'Languages', color: '#E87722' },
              ].map((s, i) => (
                <div key={i}>
                  <p className="text-2xl xl:text-3xl font-bold" style={{ color: s.color }}>{s.num}</p>
                  <p className="text-[11px] text-white/30 mt-0.5 uppercase tracking-wider">{s.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" style={{ color: '#B0CB1F' }} />
            <p className="text-[11px] text-white/20">Powered by Claude Sonnet 4.5 + OpenAI TTS</p>
          </div>
        </div>
      </div>

      {/* Right Form Panel */}
      <div className="flex-1 flex flex-col" style={{ backgroundColor: theme === 'dark' ? '#333333' : '#FFFFFF' }}>
        {/* Top bar */}
        <div className="flex items-center justify-between p-5 sm:p-7">
          <div className="lg:hidden">
            <img src={theme === 'dark' ? LOGO_LIGHT : LOGO_DARK} alt="Brainayan" className="h-7 w-auto" data-testid="login-logo-mobile" />
          </div>
          <div className="lg:ml-auto" />
          <button data-testid="login-theme-toggle" onClick={toggleTheme}
            className="w-9 h-9 rounded-lg flex items-center justify-center transition-colors"
            style={{ border: `1px solid ${theme === 'dark' ? '#404040' : '#E5E7EB'}`, color: theme === 'dark' ? '#E87722' : '#5B5B5B' }}>
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
        </div>

        {/* Form container */}
        <div className="flex-1 flex items-center justify-center px-6 sm:px-8 pb-16">
          <div className="w-full max-w-sm">
            <div className="mb-10">
              <h2 className="text-2xl font-bold tracking-tight" style={{ color: theme === 'dark' ? '#F5F5F5' : '#333333', fontFamily: 'system-ui' }}>
                {isLogin ? 'Welcome back' : 'Create account'}
              </h2>
              <p className="text-sm mt-2" style={{ color: '#5B5B5B' }}>
                {isLogin ? 'Sign in to continue your training' : 'Start your soft skills journey'}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {!isLogin && (
                <div>
                  <label className="text-sm font-medium mb-2 block" style={{ color: theme === 'dark' ? '#F5F5F5' : '#333333' }}>Full Name</label>
                  <input data-testid="register-name-input" type="text" value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full h-11 px-4 rounded-lg text-sm outline-none transition-all"
                    style={{
                      backgroundColor: theme === 'dark' ? '#2A2A2A' : '#F5F5F5',
                      color: theme === 'dark' ? '#F5F5F5' : '#333333',
                      border: '2px solid transparent',
                    }}
                    onFocus={(e) => { e.target.style.borderColor = '#B0CB1F'; e.target.style.boxShadow = '0 0 0 3px rgba(176,203,31,0.15)'; }}
                    onBlur={(e) => { e.target.style.borderColor = 'transparent'; e.target.style.boxShadow = 'none'; }}
                    placeholder="Your full name" required />
                </div>
              )}
              <div>
                <label className="text-sm font-medium mb-2 block" style={{ color: theme === 'dark' ? '#F5F5F5' : '#333333' }}>Email</label>
                <input data-testid="email-input" type="email" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full h-11 px-4 rounded-lg text-sm outline-none transition-all"
                  style={{
                    backgroundColor: theme === 'dark' ? '#2A2A2A' : '#F5F5F5',
                    color: theme === 'dark' ? '#F5F5F5' : '#333333',
                    border: '2px solid transparent',
                  }}
                  onFocus={(e) => { e.target.style.borderColor = '#B0CB1F'; e.target.style.boxShadow = '0 0 0 3px rgba(176,203,31,0.15)'; }}
                  onBlur={(e) => { e.target.style.borderColor = 'transparent'; e.target.style.boxShadow = 'none'; }}
                  placeholder="you@company.com" required />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium" style={{ color: theme === 'dark' ? '#F5F5F5' : '#333333' }}>Password</label>
                  {isLogin && (
                    <button type="button" data-testid="forgot-password-link"
                      onClick={() => { setForgotEmail(email); setShowForgot(true); }}
                      className="text-xs font-medium hover:underline underline-offset-2" style={{ color: '#2BA5A5' }}>
                      Forgot password?
                    </button>
                  )}
                </div>
                <input data-testid="password-input" type="password" value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full h-11 px-4 rounded-lg text-sm outline-none transition-all"
                  style={{
                    backgroundColor: theme === 'dark' ? '#2A2A2A' : '#F5F5F5',
                    color: theme === 'dark' ? '#F5F5F5' : '#333333',
                    border: '2px solid transparent',
                  }}
                  onFocus={(e) => { e.target.style.borderColor = '#B0CB1F'; e.target.style.boxShadow = '0 0 0 3px rgba(176,203,31,0.15)'; }}
                  onBlur={(e) => { e.target.style.borderColor = 'transparent'; e.target.style.boxShadow = 'none'; }}
                  placeholder="Min 6 characters" required minLength={6} />
              </div>

              <Button data-testid="auth-submit-button" type="submit" disabled={loading}
                className="w-full h-12 font-semibold rounded-lg text-sm gap-2 active:scale-[0.98] transition-transform"
                style={{ backgroundColor: '#B0CB1F', color: '#333333' }}>
                {loading ? 'Please wait...' : isLogin ? 'Sign In' : 'Create Account'}
                {!loading && <ArrowRight className="w-4 h-4" />}
              </Button>
            </form>

            <div className="mt-8 text-center">
              <button data-testid="toggle-auth-mode" onClick={() => setIsLogin(!isLogin)}
                className="text-sm transition-colors" style={{ color: '#5B5B5B' }}>
                {isLogin ? "Don't have an account? " : 'Already have an account? '}
                <span className="font-semibold underline underline-offset-4" style={{ color: '#2BA5A5' }}>
                  {isLogin ? 'Sign up' : 'Sign in'}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgot} onOpenChange={setShowForgot}>
        <DialogContent className="border shadow-xl max-w-sm" style={{ backgroundColor: theme === 'dark' ? '#2A2A2A' : '#FFFFFF', borderColor: theme === 'dark' ? '#404040' : '#E5E7EB' }} data-testid="forgot-password-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold flex items-center gap-2" style={{ color: theme === 'dark' ? '#F5F5F5' : '#333333' }}>
              <Mail className="w-5 h-5" style={{ color: '#2BA5A5' }} /> Forgot Password
            </DialogTitle>
            <DialogDescription className="text-sm" style={{ color: '#5B5B5B' }}>Enter your email to get a reset token</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <input data-testid="forgot-email-input" type="email" value={forgotEmail}
              onChange={(e) => setForgotEmail(e.target.value)}
              className="w-full h-10 px-3 rounded-lg text-sm outline-none"
              style={{ backgroundColor: theme === 'dark' ? '#1A1A1A' : '#F5F5F5', color: theme === 'dark' ? '#F5F5F5' : '#333333', border: '2px solid transparent' }}
              onFocus={(e) => { e.target.style.borderColor = '#B0CB1F'; }}
              onBlur={(e) => { e.target.style.borderColor = 'transparent'; }}
              placeholder="your@email.com" />
            <div className="flex gap-2">
              <Button onClick={() => setShowForgot(false)} className="flex-1 h-9 text-sm rounded-lg"
                style={{ backgroundColor: 'transparent', border: `1px solid ${theme === 'dark' ? '#404040' : '#E5E7EB'}`, color: theme === 'dark' ? '#F5F5F5' : '#5B5B5B' }}>Cancel</Button>
              <Button data-testid="forgot-submit-btn" onClick={handleForgotPassword} disabled={forgotLoading}
                className="flex-1 h-9 text-sm rounded-lg font-semibold" style={{ backgroundColor: '#B0CB1F', color: '#333333' }}>
                {forgotLoading ? 'Sending...' : 'Get Reset Token'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showReset} onOpenChange={(open) => { if (!open) { setShowReset(false); setResetSuccess(false); } }}>
        <DialogContent className="border shadow-xl max-w-sm" style={{ backgroundColor: theme === 'dark' ? '#2A2A2A' : '#FFFFFF', borderColor: theme === 'dark' ? '#404040' : '#E5E7EB' }} data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold flex items-center gap-2" style={{ color: theme === 'dark' ? '#F5F5F5' : '#333333' }}>
              <KeyRound className="w-5 h-5" style={{ color: '#E87722' }} /> Reset Password
            </DialogTitle>
            <DialogDescription className="text-sm" style={{ color: '#5B5B5B' }}>
              {resetSuccess ? 'Password reset successfully!' : 'Enter the token and your new password'}
            </DialogDescription>
          </DialogHeader>
          {resetSuccess ? (
            <div className="text-center py-4">
              <CheckCircle className="w-12 h-12 mx-auto mb-3" style={{ color: '#2BA5A5' }} />
              <p className="text-sm font-bold mb-1" style={{ color: theme === 'dark' ? '#F5F5F5' : '#333333' }}>Password Updated</p>
              <p className="text-xs mb-4" style={{ color: '#5B5B5B' }}>You can now sign in with your new password</p>
              <Button onClick={() => { setShowReset(false); setResetSuccess(false); }}
                className="h-9 px-6 text-sm rounded-lg font-semibold" style={{ backgroundColor: '#B0CB1F', color: '#333333' }}>
                Back to Login
              </Button>
            </div>
          ) : (
            <div className="space-y-3 mt-2">
              <div>
                <label className="text-xs mb-1 block" style={{ color: '#5B5B5B' }}>Reset Token</label>
                <input data-testid="reset-token-input" type="text" value={resetTokenInput}
                  onChange={(e) => setResetTokenInput(e.target.value)}
                  className="w-full h-10 px-3 rounded-lg text-sm font-mono outline-none"
                  style={{ backgroundColor: theme === 'dark' ? '#1A1A1A' : '#F5F5F5', color: theme === 'dark' ? '#F5F5F5' : '#333333' }}
                  placeholder="Paste reset token" />
              </div>
              <div>
                <label className="text-xs mb-1 block" style={{ color: '#5B5B5B' }}>New Password</label>
                <input data-testid="reset-new-password" type="password" value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full h-10 px-3 rounded-lg text-sm outline-none"
                  style={{ backgroundColor: theme === 'dark' ? '#1A1A1A' : '#F5F5F5', color: theme === 'dark' ? '#F5F5F5' : '#333333' }}
                  placeholder="Min 6 characters" />
              </div>
              <div>
                <label className="text-xs mb-1 block" style={{ color: '#5B5B5B' }}>Confirm Password</label>
                <input data-testid="reset-confirm-password" type="password" value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full h-10 px-3 rounded-lg text-sm outline-none"
                  style={{ backgroundColor: theme === 'dark' ? '#1A1A1A' : '#F5F5F5', color: theme === 'dark' ? '#F5F5F5' : '#333333' }}
                  placeholder="Confirm password" />
              </div>
              <div className="flex gap-2 pt-1">
                <Button onClick={() => setShowReset(false)} className="flex-1 h-9 text-sm rounded-lg"
                  style={{ backgroundColor: 'transparent', border: `1px solid ${theme === 'dark' ? '#404040' : '#E5E7EB'}`, color: theme === 'dark' ? '#F5F5F5' : '#5B5B5B' }}>Cancel</Button>
                <Button data-testid="reset-submit-btn" onClick={handleResetPassword} disabled={resetLoading}
                  className="flex-1 h-9 text-sm rounded-lg font-semibold" style={{ backgroundColor: '#B0CB1F', color: '#333333' }}>
                  {resetLoading ? 'Resetting...' : 'Reset Password'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
